import {action, computed, observable, reaction} from 'mobx';

import {lazyInitialize, nonenumerable} from 'core-decorators';
import APIRequestState from './_util/api-request';

import {withLocalStorage} from 'spa/Components/Context';

class ValidateBasketState extends APIRequestState {
  putRequest(queryParams, ...params) {
    return this.ctx.sdk.post(`/orders/basketing/validate?${queryParams}`, ...params);
  }
}

class GetBasketCategoriesState extends APIRequestState {
  putRequest(queryParams, ...params) {
    return this.ctx.sdk.post(`/orders/basketing/categories?${queryParams}`, ...params);
  }
}

class GetBasketCategorySpecialitiesState extends APIRequestState {
  putRequest(urlId, ...params) {
    return this.ctx.sdk.post(`/orders/basketing/categories/${urlId}/specialities`, ...params);
  }
}

@withLocalStorage
class BasketState {
  @lazyInitialize @nonenumerable getBasketCategories = new GetBasketCategoriesState(this.ctx);
  @lazyInitialize @nonenumerable getBasketCategorySpecialities = new GetBasketCategorySpecialitiesState(this.ctx);
  @lazyInitialize @nonenumerable validateBasket = new ValidateBasketState(this.ctx);

  @observable basketItems = [];
  @observable basketPlaceId = undefined;
  @observable basketServiceArea = undefined;
  @observable isDigitalBasket = false;
  @observable listenerSet = false;
  @observable validatedBasket = null;
  @observable validatingDigitalBasket = false;
  @observable workerCount = 1;

  @action addToBasket(items) {
    const {localStorageHelper} = this.ctx;

    let storageBasketItems;
    let tempBasket;

    if (this.isDigitalBasket) {
      this.reset();
    }

    if (localStorageHelper.isLocalStorageActive) {
      // Check the localstorage, basket might've been updated in another tab, therefore is the source of truth
      storageBasketItems = localStorageHelper.getItem('basketItems');

      tempBasket = items;
    } else {
      // User disabled localStorage, so current basketItems in the state is the source of truth
      tempBasket = this.basketItems.length ? this.basketItems.concat(items) : items;
    }

    if (storageBasketItems && storageBasketItems.length) {
      tempBasket = storageBasketItems.concat(tempBasket);
    }

    this.basketItems = tempBasket;

    if (localStorageHelper.isLocalStorageActive) {
      const {address, selection} = this.ctx;

      const storageBasketAddressServiceArea = localStorageHelper.getItem('basketAddressServiceArea');
      const storageBasketAddressPlaceId = localStorageHelper.getItem('basketAddressPlaceId');
      const storageBasketIsDigital = localStorageHelper.getItem('basketDigital');

      const serviceAreaId = address && address.response && address.response.serviceArea && address.response.serviceArea.id;

      if (storageBasketAddressServiceArea !== serviceAreaId) {
        localStorageHelper.setItem('basketAddressServiceArea', serviceAreaId);
      }

      if (storageBasketAddressPlaceId !== selection.place_id) {
        localStorageHelper.setItem('basketAddressPlaceId', selection.place_id);
      }

      if (storageBasketIsDigital !== this.isDigitalBasket) {
        localStorageHelper.setItem('basketDigital', this.isDigitalBasket);
      }

      this.basketPlaceId = selection.place_id;
      this.basketServiceArea = serviceAreaId;

      localStorageHelper.setItem('basketItems', this.basketItems);
    }
  }

  @action reset() {
    this.basketItems = [];
    this.isDigitalBasket = false;
    this.workerCount = 1;
    this.validatedBasket = null;
    this.isDigitalBasket = false;

    const {localStorageHelper} = this.ctx;

    if (localStorageHelper.isLocalStorageActive) {
      localStorageHelper.removeItem('basketItems');
      localStorageHelper.removeItem('basketWorkerCount');
      localStorageHelper.removeItem('basketAddressServiceArea');
      localStorageHelper.removeItem('basketAddressPlaceId');
    }
  }

  @computed get basketError() {
    const {validatedBasket} = this;
    if (!validatedBasket) return null;

    const {basketError} = validatedBasket;

    return basketError;
  }

  @computed get basketInfo() {
    const {basketItems, workerCount} = this;

    return {
      basket: basketItems,
      workerCount,
    };
  }

  @computed get basketItemsCount() {
    const {validatedBasket} = this;
    if (!validatedBasket) return null;

    return validatedBasket.basket ? validatedBasket.basket.length : 0;
  }

  @computed get canAddTreatments() {
    const {validatedBasket} = this;
    if (!validatedBasket) return false;

    return validatedBasket.canAddAdditionalTreatments;
  }

  @computed get canDoParallel() {
    const {validatedBasket} = this;
    if (!validatedBasket) return null;

    return validatedBasket.showParallelOption;
  }

  @computed get isBasketEmpty() {
    const {basketItems} = this;

    return basketItems.length === 0;
  }

  @computed get parallelOptionLabel() {
    const {validatedBasket} = this;
    if (!validatedBasket) return null;

    return validatedBasket.parallelOptionLabel;
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners.push(
      reaction(
        () => this.isDigitalBasket,
        () => {
          const {localStorageHelper} = this.ctx;

          localStorageHelper.setItem('basketDigital', this.isDigitalBasket);
        },
        {
          name: 'Update isDigitalBasket in localstorage',
        },
      ),
      reaction(
        () => this.basketItems.length,
        () => {
          const {localStorageHelper} = this.ctx;

          if (localStorageHelper.isLocalStorageActive) {
            localStorageHelper.setItem('basketItems', this.basketItems);
            localStorageHelper.setItem('basketDigital', this.isDigitalBasket);

            if (!this.basketItems.length) {
              localStorageHelper.removeItem('basketAddressServiceArea');
              localStorageHelper.removeItem('basketAddressPlaceId');
              localStorageHelper.removeItem('basketDigital');
            }
          }
        },
        {
          name: 'Update basketItems in localstorage',
        },
      ),
      reaction(
        () => this.workerCount,
        () => {
          const {localStorageHelper} = this.ctx;

          if (localStorageHelper.isLocalStorageActive) {
            localStorageHelper.setItem('basketWorkerCount', this.workerCount);
          }
        },
        {
          name: 'Update workerCount in localstorage',
        },
      ),
      reaction(
        () => this.ctx.localStorageHelper.isLocalStorageActive,
        (isLocalStorageActive) => {
          const {localStorageHelper} = this.ctx;

          if (isLocalStorageActive) {
            const storageBasketItems = localStorageHelper.getItem('basketItems');
            const storageBasketWorkerCount = localStorageHelper.getItem('basketWorkerCount');
            const storageBasketAddressServiceArea = localStorageHelper.getItem('basketAddressServiceArea');
            const storageBasketAddressPlaceId = localStorageHelper.getItem('basketAddressPlaceId');
            const storageBasketIsDigital = localStorageHelper.getItem('basketDigital');

            if (storageBasketItems) {
              this.basketItems = storageBasketItems;
            }

            if (storageBasketWorkerCount) {
              this.workerCount = storageBasketWorkerCount;
            }

            if (storageBasketIsDigital) {
              this.isDigitalBasket = storageBasketIsDigital;
            }

            if (storageBasketAddressPlaceId) {
              this.ctx.selection.place_id = storageBasketAddressPlaceId;
              this.ctx.selection.address_id = undefined;

              this.basketPlaceId = storageBasketAddressPlaceId;
              this.basketServiceArea = storageBasketAddressServiceArea;

              this.ctx.address.load(storageBasketAddressPlaceId);
            }

            if (!this.listenerSet) {
              // Listener for localStorage changes in other tabs
              window.addEventListener('storage', (event) => {
                const acceptedStorageItems = [
                  'basketAddressPlaceId',
                  'basketAddressServiceArea',
                  'basketItems',
                  'basketWorkerCount',
                ];

                if (acceptedStorageItems.includes(event.key)) {
                  const listenerStorageBasketItems = localStorageHelper.getItem('basketItems');
                  const listenerStorageBasketWorkerCount = localStorageHelper.getItem('basketWorkerCount');
                  const listenerStorageBasketAddressServiceArea = localStorageHelper.getItem('basketAddressServiceArea');
                  const listenerStorageBasketAddressPlaceId = localStorageHelper.getItem('basketAddressPlaceId');
                  const listenerStorageBasketIsDigital = localStorageHelper.getItem('basketDigital');

                  if (listenerStorageBasketItems) {
                    if (listenerStorageBasketAddressPlaceId) {
                      this.basketPlaceId = listenerStorageBasketAddressPlaceId;
                      this.basketServiceArea = listenerStorageBasketAddressServiceArea;

                      this.ctx.selection.place_id = listenerStorageBasketAddressPlaceId;

                      this.ctx.address.load(listenerStorageBasketAddressPlaceId);
                    }

                    this.basketItems = listenerStorageBasketItems;
                    this.isDigitalBasket = listenerStorageBasketIsDigital;
                  }

                  if (listenerStorageBasketWorkerCount) {
                    this.workerCount = listenerStorageBasketWorkerCount;
                  }
                }
              });

              this.listenerSet = true;
            }
          }
        },
        {
          fireImmediately: true,
          name: 'Check when localstorage state changes',
        },
      ),
    );
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default BasketState;



// WEBPACK FOOTER //
// ./spa/state/basket.jsx